import type { Product } from '@/types/Product.types';
import type {
  AddProductResponse,
  ApiResponse,
  RemoveProductResponse,
  SlaylistPreview,
} from '@/types/Wishlist';
import { isUserSigned } from '@/utils/getUserInfo';
import slaydApi from './axiosInstance';

// Utility to check if user is authenticated by checking for token in localStorage
const isAuthenticated = (): boolean => {
  return Boolean(isUserSigned());
};

/**
 * Add a product to a list using the unified endpoint. Creates the list if it doesn't exist.
 * - If list_name is "Saved", adds only to Saved list
 * - If list_name is anything else, adds to both the custom list and Saved list
 */
export const addProductToList = async (
  productId: number,
  listName: string
): Promise<AddProductResponse | null> => {
  if (!isAuthenticated()) {
    console.warn('User not authenticated');
    return null;
  }
  try {
    const response = await slaydApi.post<AddProductResponse>(
      '/activity/add-product/',
      {
        product_id: productId,
        list_name: listName,
      }
    );

    if (!response.data) throw new Error('Invalid response');

    return response.data;
  } catch (error) {
    console.error('Error adding product to list:', error);
    throw new Error('Failed to add product to list');
  }
};

/**
 * Fetch all lists for the authenticated user with their top products
 */
export const getUserListsPreview = async (): Promise<
  SlaylistPreview[] | null
> => {
  if (!isAuthenticated()) {
    console.warn('User not authenticated');
    return null;
  }
  try {
    const response = await slaydApi.get<SlaylistPreview[]>(
      '/activity/get-lists/'
    );

    if (!response.data) throw new Error('Invalid response');

    return response.data;
  } catch (error) {
    console.error('Error fetching user lists:', error);
    throw new Error('Failed to fetch user lists');
  }
};

/**
 * Fetch all products in a specific list
 */
export const getListProducts = async (
  listId: number
): Promise<Product[] | null> => {
  if (!isAuthenticated()) {
    console.warn('User not authenticated');
    return null;
  }
  try {
    const response = await slaydApi.get<Product[]>(
      `/activity/get-products/${listId}/`
    );

    if (!response.data) throw new Error('Invalid response');

    return response.data;
  } catch (error) {
    console.error('Error fetching list products:', error);
    throw new Error('Failed to fetch list products');
  }
};

/**
 * Delete a specified list (except the Saved list)
 */
export const deleteList = async (
  listId: number
): Promise<ApiResponse | null> => {
  if (!isAuthenticated()) {
    console.warn('User not authenticated');
    return null;
  }
  try {
    const response = await slaydApi.delete<ApiResponse>(
      `/activity/delete-list/${listId}/`
    );

    if (!response.data) throw new Error('Invalid response');

    return response.data;
  } catch (error) {
    console.error('Error deleting list:', error);
    throw new Error('Failed to delete list');
  }
};

/**
 * Remove a product from a list using the unified removal endpoint
 * - If removing from Saved list: Only allowed if product is not in any other list
 * - If removing from custom list: Removes from custom list and from Saved if not in other lists
 */
export const removeProductFromList = async (
  listId: number,
  productId: number
): Promise<RemoveProductResponse | null> => {
  if (!isAuthenticated()) {
    console.warn('User not authenticated');
    return null;
  }
  try {
    const response = await slaydApi.delete<RemoveProductResponse>(
      `/activity/remove-product/${listId}/${productId}/`
    );

    if (!response.data) throw new Error('Invalid response');

    return response.data;
  } catch (error) {
    console.error('Error removing product from list:', error);
    throw new Error('Failed to remove product from list');
  }
};

// Utility functions

/**
 * Add product to Saved list specifically
 */
export const addToSaved = async (
  productId: number
): Promise<AddProductResponse | null> => {
  return addProductToList(productId, 'Saved');
};

// Get a list by name
export const getListByName = (
  lists: SlaylistPreview[],
  listName: string
): SlaylistPreview | undefined => {
  return lists.find((list) => list.name === listName);
};
